import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoMdPause } from 'react-icons/io';
import { BsThreeDots } from 'react-icons/bs';
import OfferCardStat from '../OfferStat';
import { Avatar, AvatarSizes } from 'components/Avatar';
import { MdCopyAll } from 'react-icons/md';
import { FaCirclePlay } from 'react-icons/fa6';
import { PromoterOfferContractDto } from 'legacy/events/types';
import { showError, showToast } from 'legacy/common/utils/toast';
import { currencyFormat } from 'legacy/common/utils/currency';
import PromoterActionModal from '../modals/PromoterActionModal';
import {
  useGetOffer,
  useGetOffers,
  usePausePromoterContract
} from 'legacy/events/hooks';

interface PromoterCardProps {
  contract: PromoterOfferContractDto;
  eventId: string;
}

const PromoterCard = ({ contract, eventId }: PromoterCardProps) => {
  const { t } = useTranslation(['events']);
  const { refetch } = useGetOffers(eventId, { enabled: false });
  const { refetch: refetchOffer } = useGetOffer(contract.offer.id, {
    enabled: false
  });
  const { promoter, offer, analytics, attributionCode, attributionUrl } =
    contract;
  const { name, photoUrl } = promoter;
  const { name: offerName } = offer;
  const { views, sales, revenue, earnings } = analytics;
  const [isPromoterActionModalOpen, setIsPromoterActionModalOpen] =
    useState(false);

  const isPaused = !contract.isActive;
  const pausePromoterContract = usePausePromoterContract();

  const handleClick = (e) => {
    e.stopPropagation();
    setIsPromoterActionModalOpen(true);
  };

  const handlePausePromoter = (
    e?: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e?.stopPropagation();
    pausePromoterContract.mutate(
      {
        contractId: contract.id,
        isActive: !contract.isActive
      },
      {
        onSuccess: () => {
          setIsPromoterActionModalOpen(false);
          refetch();
          refetchOffer();
        },
        onError: (error) => {
          showError({
            title: t('settings.promoterOffers.promoterCard.errorPausingPromoter'),
            message: error?.message || t('settings.promoterOffers.promoterCard.anErrorOccurred')
          });
        }
      }
    );
  };

  const handleCopyAttributionCode = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(attributionCode);
    showToast({
      message: t('settings.promoterOffers.promoterCard.copiedToClipboard'),
      type: 'success'
    });
  };

  const handleCopyAttributionUrl = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(attributionUrl);
    showToast({
      message: t('settings.promoterOffers.promoterCard.copiedToClipboard'),
      type: 'success'
    });
  };

  return (
    <div
      className={`border-1 border-[#EBEBEB] mt-4 rounded-lg ${
        isPaused ? 'bg-[#F6F6F6]' : ''
      }`}
    >
      <div className="p-4">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center">
            <Avatar src={photoUrl} size={AvatarSizes.Md} className="mr-2" />
            <div className="font-bold text-base">{name}</div>
          </div>
          <BsThreeDots
            className="text-[#787878]"
            size={20}
            onClick={handleClick}
          />
        </div>
        <div className="flex items-center gap-2 pt-4 pb-2">
          <div className="text-sm font-normal">🤝</div>
          <div className="text-sm font-normal text-[#202020]">{offerName}</div>
        </div>
        <div className="flex items-center gap-2 pb-4">
          <div className="text-sm font-normal">🗣️</div>
          <div className="flex flex-row items-center">
            <div className="text-sm font-normal text-[#202020]">
              {t('settings.promoterOffers.promoterCard.promoterCode')}
            </div>
            <div
              className={`text-sm font-bold ${
                isPaused ? 'text-[#575757]' : 'text-[#3853C4]'
              } ml-1`}
              onClick={isPaused ? undefined : handleCopyAttributionCode}
            >
              {attributionCode}
            </div>
            <MdCopyAll
              className={`${
                isPaused ? 'text-[#575757]' : 'text-[#3853C4]'
              } ml-1`}
              size={16}
              onClick={isPaused ? undefined : handleCopyAttributionCode}
            />
          </div>
        </div>
        <div className="grid grid-cols-[1fr_96px] h-[44px] mb-4">
          <div
            className={`flex items-center font-normal text-[#202020] italic border ${
              isPaused ? 'border-[#DCDCDC]' : 'border-[#EFF2FF]'
            } rounded-tl-[100px] rounded-bl-[100px] h-[44px] overflow-hidden min-w-0`}
          >
            <div className="ml-2 text-sm truncate w-full">{attributionUrl}</div>
          </div>
          <div className="h-[44px]">
            <div
              className={`flex items-center justify-center ${
                isPaused ? 'bg-[#DCDCDC]' : 'bg-[#3853C4]'
              } h-[44px] border-r-1 ${
                isPaused ? 'border-[#DCDCDC]' : 'border-[#3853C4]'
              } rounded-tr-[100px] rounded-br-[100px]`}
            >
              <div
                className={`text-sm font-bold ${
                  isPaused ? 'text-[#575757]' : 'text-white'
                }`}
                onClick={isPaused ? undefined : handleCopyAttributionUrl}
              >
                {t('settings.promoterOffers.promoterCard.copy')}
              </div>
              <MdCopyAll
                className={`${isPaused ? 'text-[#575757]' : 'text-white'} ml-2`}
                size={16}
              />
            </div>
          </div>
        </div>
        <div
          className={`flex items-center justify-center gap-2 border ${
            isPaused ? '' : 'border-[#EFF2FF]'
          } rounded-2xl p-2 ${isPaused ? 'bg-[#EFF2FF]' : ''}`}
          onClick={(e) => handlePausePromoter(e)}
        >
          <div className="text-sm font-bold text-[#3853C4]">
            {isPaused ? t('settings.promoterOffers.promoterCard.resumePromoterLink') : t('settings.promoterOffers.promoterCard.pausePromoterLink')}
          </div>
          {!isPaused ? (
            <IoMdPause className="text-[#3853C4]" />
          ) : (
            <FaCirclePlay className="text-[#3853C4]" />
          )}
        </div>
      </div>
      <div
        className={`h-px ${isPaused ? 'bg-[#DCDCDC]' : 'bg-[#EBEBEB]'} w-full`}
      />
      <div className="flex flex-row items-center justify-between p-4">
        <OfferCardStat title={t('settings.promoterOffers.promoterCard.views')} description={views.toString()} />
        <OfferCardStat title={t('settings.promoterOffers.promoterCard.sales')} description={sales.toString()} />
        <OfferCardStat title={t('settings.promoterOffers.promoterCard.revenue')} description={currencyFormat(revenue)} />
        <OfferCardStat
          title={t('settings.promoterOffers.promoterCard.earnings')}
          description={currencyFormat(earnings)}
        />
      </div>
      {isPromoterActionModalOpen && (
        <PromoterActionModal
          onClose={() => setIsPromoterActionModalOpen(false)}
          promoterId={promoter.id}
          handlePausePromoter={handlePausePromoter}
          isPaused={isPaused}
        />
      )}
    </div>
  );
};

export default PromoterCard;
